/* My Work: the personal attention queue (incidents, monitors, brain tasks, Linear). This widget is its only UI;
   /my-work redirects here. Linear issues are read-only: they leave the queue when closed in Linear. */
import type { inferRouterOutputs } from "@trpc/server"
import { useCallback } from "react"
import toast from "react-hot-toast"

import type { AppRouter } from "../../../../../aixinternal-api/src/router/_app"
import { trpc } from "../../../lib/trpc"
import { ago, worstVerdict } from "../homeDashboardModel"
import { EVerdict } from "../homeDashboard.types"
import type { IRowAction, IWidgetDefinition, IWidgetResult, IWidgetRow } from "../homeDashboard.types"
import { KeyValues } from "./WidgetParts"
import { WIDGET_QUERY_OPTIONS, useQueryWidget } from "./widgetQuery"

type TMyWork = inferRouterOutputs<AppRouter>["myWork"]["getQueue"]
type TAttentionItem = TMyWork["attentionItems"][number]
type TActionsFor = (item: TAttentionItem) => IRowAction[]

const PRIORITY_VERDICT: Record<string, EVerdict> = { p0: EVerdict.ACT, p1: EVerdict.ACT, p2: EVerdict.WATCH }

const SOURCE_LABEL: Record<string, string> = {
  incident: "Incident",
  monitor: "Monitor",
  brain_task: "Task",
  access: "Access",
  deploy: "Deploy",
  github: "GitHub",
  linear: "Linear",
  manual: "Manual",
}

const metaString = (item: TAttentionItem, key: string) => {
  const value = item.metadata[key]
  return typeof value === "string" ? value : null
}

/** Lifecycle mutations as row actions (a ack · s snooze · e resolve · x dismiss). */
const useMyWorkActions = (): TActionsFor => {
  const utils = trpc.useUtils()
  const done = (label: string) => ({
    onSuccess: () => {
      toast.success(label)
      void utils.myWork.getQueue.invalidate()
    },
    onError: (error: { message: string }) => toast.error(error.message || `${label} failed`),
  })
  const ack = trpc.myWork.acknowledge.useMutation(done("Acknowledged"))
  const snooze = trpc.myWork.snooze.useMutation(done("Snoozed 4h"))
  const resolve = trpc.myWork.resolve.useMutation(done("Resolved"))
  const dismiss = trpc.myWork.dismiss.useMutation(done("Dismissed"))
  const busy = ack.isLoading || snooze.isLoading || resolve.isLoading || dismiss.isLoading

  return useCallback(
    (item: TAttentionItem) => {
      // Transient items are derived on read and have no row to update
      const disabled = busy || item.id.startsWith("transient:")
      const actions: IRowAction[] = [
        { key: "a", label: "Ack", disabled, run: () => ack.mutate({ id: item.id }) },
        { key: "s", label: "Snooze 4h", disabled, run: () => snooze.mutate({ id: item.id, duration: "4h" }) },
      ]
      // Linear owns completion; resolving here would hide an issue that is still open there
      if (item.source !== "linear")
        actions.push({ key: "e", label: "Resolve", disabled, run: () => resolve.mutate({ id: item.id }) })
      actions.push({ key: "x", label: "Dismiss", disabled, run: () => dismiss.mutate({ id: item.id }) })
      return actions
    },
    [busy, ack.mutate, snooze.mutate, resolve.mutate, dismiss.mutate],
  )
}

const toRow = (item: TAttentionItem, now: number, actionsFor: TActionsFor): IWidgetRow => {
  const stale = !!item.staleAt && new Date(item.staleAt).getTime() < now
  const verdict = stale ? EVerdict.ACT : (PRIORITY_VERDICT[item.priority] ?? EVerdict.UNKNOWN)
  const isLinear = item.source === "linear"
  return {
    key: `${item.type}:${item.id}`,
    verdict,
    pill: { text: item.priority, verdict },
    title: item.title,
    sub: [
      SOURCE_LABEL[item.source] ?? item.source,
      item.companyName ?? item.project,
      isLinear ? metaString(item, "linearState") : item.requiredAction.replace(/_/g, " "),
    ]
      .filter(Boolean)
      .join(" · "),
    meta: item.dueAt ? `due ${ago(item.dueAt, now)}` : ago(item.sourceCreatedAt, now),
    href: item.sourceUrl ?? undefined,
    hrefLabel: isLinear ? "Open in Linear" : "Open source",
    // Linear rows roll up by team ("VidMax"); everything else by its company or project slug
    company: (isLinear ? metaString(item, "teamName") : item.companyName) ?? item.project,
    actions: actionsFor(item),
    details: (
      <>
        {item.summary && <p>{item.summary}</p>}
        <KeyValues
          pairs={[
            ["Type", item.type.replace(/_/g, " ").toLowerCase()],
            ["Status", item.status],
            ["Priority", isLinear ? metaString(item, "linearPriority") : null],
            ["Due", item.dueAt ? new Date(item.dueAt).toLocaleDateString() : null],
            ["Stale", item.staleAt ? ago(item.staleAt, now) : null],
          ]}
        />
      </>
    ),
  }
}

const renderMyWork = (data: TMyWork, actionsFor: TActionsFor): IWidgetResult => {
  const now = Date.now()
  const rows = data.attentionItems.map((item) => toRow(item, now, actionsFor))
  const { summary } = data
  return {
    verdict: worstVerdict(rows.map((r) => r.verdict)),
    count: `${summary.attentionItems} items · ${summary.staleItems} stale`,
    lead: (
      <>
        {[
          `${summary.linearIssues} Linear`,
          `${summary.assignedTasks} tasks`,
          `${summary.assignedIncidents + summary.companyIncidents} incidents`,
          `${summary.monitorFailures} monitor failures`,
          `${summary.companies} companies`,
        ].join(" · ")}
        {data.linearError && <p className="hd-error">Linear unavailable: {data.linearError}</p>}
      </>
    ),
    rows,
    empty: "Nothing needs you.",
    foot: `Generated ${ago(data.generatedAt, now)}`,
  }
}

export const myWorkWidget: IWidgetDefinition = {
  id: "my-work",
  title: "My Work",
  description: "Reviews, incidents, escalations, tasks and Linear issues waiting on you",
  columns: { 2: 2, 3: 2, 4: 2, 5: 3, 6: 3 },
  useWidget: () => {
    const actionsFor = useMyWorkActions()
    const render = useCallback((data: TMyWork) => renderMyWork(data, actionsFor), [actionsFor])
    return useQueryWidget(trpc.myWork.getQueue.useQuery(undefined, WIDGET_QUERY_OPTIONS), render)
  },
}
